import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth, API } from "../App";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import axios from "axios";
import { 
  Plus, FlaskConical, Edit2, Trash2,
  DollarSign, Package, CheckCircle, Copy, Download
} from "lucide-react";

export default function MyRecipesPage() {
  const { token } = useAuth();
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [batchMultiplier, setBatchMultiplier] = useState(1);
  const [makingBatch, setMakingBatch] = useState(false);
  const [checkedIngredients, setCheckedIngredients] = useState({}); // Track which ingredients are checked

  const [deleteRecipeId, setDeleteRecipeId] = useState(null);

  useEffect(() => {
    fetchRecipes();
  }, [token]);

  const fetchRecipes = async () => {
    try {
      const response = await axios.get(`${API}/recipes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecipes(response.data);
    } catch (error) {
      toast.error("Failed to fetch recipes");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteRecipeId) return;
    
    try {
      await axios.delete(`${API}/recipes/${deleteRecipeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Recipe deleted");
      setDeleteRecipeId(null);
      fetchRecipes();
    } catch (error) {
      toast.error("Failed to delete recipe");
    }
  };

  const handleMakeBatch = async () => {
    if (!selectedRecipe) return;
    
    setMakingBatch(true);
    try {
      const response = await axios.post(
        `${API}/recipes/use-materials`,
        { recipe_id: selectedRecipe.recipe_id, multiplier: batchMultiplier },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success(`Materials deducted for ${batchMultiplier}x batch of "${selectedRecipe.name}"!`);
        setSelectedRecipe(null);
        setBatchMultiplier(1);
        setCheckedIngredients({}); // Reset checkboxes
      } else {
        response.data.errors?.forEach(err => toast.error(err));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to use materials");
    } finally {
      setMakingBatch(false);
    }
  };

  // Handle ingredient checkbox changes
  const handleIngredientCheck = (ingredientIndex, checked) => {
    setCheckedIngredients(prev => ({
      ...prev,
      [ingredientIndex]: checked
    }));
  };

  // Check if all ingredients are checked
  const allIngredientsChecked = selectedRecipe 
    ? selectedRecipe.ingredients?.length > 0 && 
      selectedRecipe.ingredients.every((_, idx) => checkedIngredients[idx] === true)
    : false;

  // Reset checkboxes when recipe changes
  const handleRecipeSelect = (recipe) => {
    setSelectedRecipe(recipe);
    setCheckedIngredients({}); // Reset all checkboxes
    setBatchMultiplier(1);
  };

  const handleDuplicate = async (recipe) => {
    try {
      const duplicateData = {
        name: `${recipe.name} (Copy)`,
        description: recipe.description || "",
        total_grams: recipe.total_grams || 100,
        concentration_percent: recipe.concentration_percent || 20,
        ingredients: recipe.ingredients || [],
        notes: recipe.notes || "",
        is_public: false,
        is_for_sale: false
      };

      const response = await axios.post(
        `${API}/recipes`,
        duplicateData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.recipe_id) {
        toast.success(`Recipe duplicated: "${duplicateData.name}"`);
        fetchRecipes();
      }
    } catch (error) {
      toast.error("Failed to duplicate recipe");
    }
  };

  const handleExport = (recipe) => {
    const exportData = {
      name: recipe.name,
      description: recipe.description,
      concentration_percent: recipe.concentration_percent,
      total_grams: recipe.total_grams,
      notes: recipe.notes,
      ingredients: recipe.ingredients?.map(ing => ({
        material_name: ing.material_name,
        percentage: ing.percentage,
        grams: ing.grams,
        duration: ing.duration,
        solution_percentage: ing.solution_percentage,
        solution_type: ing.solution_type
      })) || [],
      exported_at: new Date().toISOString(),
      app: "Scentic"
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${recipe.name || 'recipe'}_scentic.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("Recipe exported!");
  };

  return (
    <div className="min-h-screen bg-noir flex">
      <Sidebar />
      
      <main className="flex-1 p-8 ml-64">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="font-playfair text-3xl font-bold text-white mb-2">
                My Recipes
              </h1>
              <p className="text-zinc-400">{recipes.length} formulas created</p>
            </div>
            <Link to="/recipe-builder">
              <Button 
                data-testid="new-recipe-btn"
                className="bg-amber hover:bg-amber-light text-black font-semibold uppercase tracking-widest text-xs"
              >
                <Plus className="w-4 h-4 mr-2" /> New Recipe
              </Button>
            </Link>
          </div>

          {/* Make Batch Dialog */}
          <Dialog open={!!selectedRecipe} onOpenChange={() => setSelectedRecipe(null)}>
            <DialogContent className="bg-noir-paper border-zinc-800">
              <DialogHeader>
                <DialogTitle className="text-white font-playfair">
                  Make Batch: {selectedRecipe?.name}
                </DialogTitle>
              </DialogHeader>
              {selectedRecipe && (
                <div className="space-y-4">
                  <p className="text-zinc-400">
                    Check off each material as you add it to your batch. Materials will be deducted from your inventory.
                  </p>
                  
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Batch Multiplier</Label>
                    <Input
                      type="number"
                      min="0.1"
                      step="0.1"
                      value={batchMultiplier}
                      onChange={(e) => setBatchMultiplier(parseFloat(e.target.value) || 1)}
                      className="bg-noir-subtle border-zinc-700 text-white font-mono"
                    />
                    <p className="text-xs text-zinc-500">
                      1 batch = {selectedRecipe.total_grams}g total
                      {batchMultiplier !== 1 && ` • ${batchMultiplier} batches = ${(selectedRecipe.total_grams * batchMultiplier).toFixed(1)}g total`}
                    </p>
                  </div>

                  <div className="p-3 bg-zinc-800/50 border border-zinc-700 space-y-3 max-h-64 overflow-y-auto">
                    <p className="text-sm text-zinc-300 font-medium">Materials to add (check each as you add):</p>
                    {selectedRecipe.ingredients?.map((ing, idx) => (
                      <div key={idx} className="flex items-center gap-3 p-2 hover:bg-zinc-700/30 rounded">
                        <Checkbox
                          checked={checkedIngredients[idx] || false}
                          onCheckedChange={(checked) => handleIngredientCheck(idx, checked)}
                          className="border-zinc-600 data-[state=checked]:bg-amber data-[state=checked]:border-amber"
                        />
                        <div className="flex-1 flex justify-between items-center">
                          <span className={`text-sm ${checkedIngredients[idx] ? 'text-green-400 line-through' : 'text-zinc-300'}`}>
                            {ing.material_name}
                          </span>
                          <span className={`text-sm font-mono ${checkedIngredients[idx] ? 'text-green-400 line-through' : 'text-amber'}`}>
                            {((ing.grams || 0) * batchMultiplier).toFixed(3)}g
                          </span>
                        </div>
                      </div>
                    ))}
                    {selectedRecipe.ingredients?.length === 0 && (
                      <p className="text-zinc-500 text-sm italic">No ingredients in this recipe</p>
                    )}
                  </div>

                  {!allIngredientsChecked && selectedRecipe.ingredients?.length > 0 && (
                    <div className="p-2 bg-yellow-500/10 border border-yellow-500/30 rounded">
                      <p className="text-yellow-400 text-sm">
                        ⚠️ Please check off all materials before proceeding with deduction
                      </p>
                    </div>
                  )}
                </div>
              )}
              <DialogFooter>
                <DialogClose asChild>
                  <Button variant="ghost" className="text-zinc-400">Cancel</Button>
                </DialogClose>
                <Button
                  onClick={handleMakeBatch}
                  disabled={makingBatch || !allIngredientsChecked}
                  className={`${
                    allIngredientsChecked 
                      ? "bg-green-600 hover:bg-green-500 text-white" 
                      : "bg-zinc-700 text-zinc-500 cursor-not-allowed"
                  }`}
                >
                  {makingBatch ? (
                    <span className="flex items-center gap-2">
                      <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Processing...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" />
                      {allIngredientsChecked ? "Make Batch & Deduct" : "Check All Materials First"}
                    </span>
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Delete Confirmation Dialog */}
          <Dialog open={!!deleteRecipeId} onOpenChange={() => setDeleteRecipeId(null)}>
            <DialogContent className="bg-noir-paper border-zinc-800">
              <DialogHeader>
                <DialogTitle className="text-white font-playfair">
                  Delete Recipe
                </DialogTitle>
              </DialogHeader>
              <p className="text-zinc-400">
                Are you sure you want to delete this recipe? This action cannot be undone.
              </p>
              <DialogFooter>
                <DialogClose asChild>
                  <Button variant="ghost" className="text-zinc-400">Cancel</Button>
                </DialogClose>
                <Button
                  onClick={handleDelete}
                  className="bg-red-600 hover:bg-red-500 text-white"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Recipes Grid */}
          {loading ? (
            <div className="text-center py-12">
              <div className="w-8 h-8 border-2 border-amber border-t-transparent rounded-full animate-spin mx-auto"></div>
            </div>
          ) : recipes.length === 0 ? (
            <Card className="bg-noir-paper border-zinc-800">
              <CardContent className="py-12 text-center">
                <FlaskConical className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                <h3 className="text-white font-medium mb-2">No recipes yet</h3>
                <p className="text-zinc-500 mb-4">Create your first formula</p>
                <Link to="/recipe-builder">
                  <Button className="bg-amber hover:bg-amber-light text-black">
                    <Plus className="w-4 h-4 mr-2" /> Create Recipe
                  </Button>
                </Link>
              </CardContent>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {recipes.map((recipe) => (
                <RecipeCard 
                  key={recipe.recipe_id}
                  recipe={recipe}
                  onDelete={() => setDeleteRecipeId(recipe.recipe_id)}
                  onMakeBatch={() => handleRecipeSelect(recipe)}
                  onDuplicate={() => handleDuplicate(recipe)}
                  onExport={() => handleExport(recipe)}
                  showActions
                />
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

const RecipeCard = ({ recipe, onDelete, onMakeBatch, onDuplicate, onExport, showActions }) => {
  // Use batch_price from recipe, or calculate from ingredients as fallback
  const batchPrice = recipe.batch_price || recipe.ingredients?.reduce((sum, ing) => {
    const grams = parseFloat(ing.grams) || 0;
    const pricePerGram = parseFloat(ing.price_per_gram) || 0;
    return sum + (grams * pricePerGram);
  }, 0) || 0;

  return (
    <Card className="bg-noir-paper border-zinc-800 hover:border-amber/30 transition-colors">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <CardTitle className="text-white font-medium truncate">{recipe.name}</CardTitle>
              {batchPrice > 0 && (
                <span className="text-green-400 font-mono text-sm flex items-center shrink-0">
                  <DollarSign className="w-3 h-3" />{batchPrice.toFixed(2)}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 text-sm mt-1">
              {recipe.concentration_percent && (
                <span className="text-zinc-500">{recipe.concentration_percent}%</span>
              )}
              <span className="text-zinc-600">•</span>
              <span className="text-zinc-500">{recipe.total_grams}g</span>
              <span className="text-zinc-600">•</span>
              <span className="text-zinc-500">{recipe.ingredients?.length || 0} ingredients</span>
            </div>
          </div>
          {showActions && (
            <div className="flex gap-1">
              <Link to={`/recipe-builder/${recipe.recipe_id}`}>
                <Button 
                  size="icon" 
                  variant="ghost" 
                  className="h-8 w-8 text-zinc-500 hover:text-white"
                  title="Edit"
                >
                  <Edit2 className="w-4 h-4" />
                </Button>
              </Link>
              <Button 
                size="icon" 
                variant="ghost" 
                onClick={onDuplicate}
                className="h-8 w-8 text-zinc-500 hover:text-blue-400"
                title="Duplicate"
              >
                <Copy className="w-4 h-4" />
              </Button>
              <Button 
                size="icon" 
                variant="ghost" 
                onClick={onExport}
                className="h-8 w-8 text-zinc-500 hover:text-purple-400"
                title="Export"
              >
                <Download className="w-4 h-4" />
              </Button>
              <Button 
                size="icon" 
                variant="ghost" 
                onClick={onDelete}
                className="h-8 w-8 text-zinc-500 hover:text-red-400"
                title="Delete"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {recipe.description && (
          <p className="text-zinc-400 text-sm mb-3 line-clamp-2">{recipe.description}</p>
        )}

        {/* Make Batch Button */}
        {recipe.ingredients?.length > 0 && (
          <Button 
            onClick={onMakeBatch}
            size="sm"
            className="w-full bg-green-600/20 hover:bg-green-600/30 text-green-400 border border-green-600/30"
          >
            <Package className="w-4 h-4 mr-2" />
            Make Batch
          </Button>
        )}
      </CardContent>
    </Card>
  );
};
